import React, { useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router'
import { generateEater } from '../../../helpers/converters'
import { useAppSelector } from '../../../store'
import { selectEater } from '../../../store/profileSlice'
import useRudderStackAnalytics from '../../../useRudderAnalytics'
import { ROUTES } from '../../../helpers/routes-helper'
import { logEvent } from '../../../analytics/analytics'

const DocumentTitle = 'Resactly — Intent Intelligence for Hospitality'

/**
 * About page — reframed around the Intent Intelligence Platform vision (2026-07). The consumer
 * "front door" that explains the company: restaurants see the meal after arrival; Resactly reads
 * how the decision to come was made. Self-contained editorial design, scoped under `.rsly-about`
 * so its type/token system never leaks into the app's MUI theme.
 */
const About: React.FC = () => {
  const [analytics, isAnalyticsReady] = useRudderStackAnalytics()
  const user = useAppSelector(selectEater)
  const location = useLocation()
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const { fullName, eaterId, email } = generateEater(user ?? {})
    if (analytics && isAnalyticsReady) {
      analytics?.page('PAGE_OPENED', {
        path: ROUTES.ABOUT,
        title: DocumentTitle,
        url: `${window.location.origin}${ROUTES.ABOUT}`,
        category: 'About',
        name: 'About Viewed',
        eaterName: fullName,
        startTime: new Date().toISOString(),
        email,
        eaterId,
      })
    }
    logEvent('page_view', { path: ROUTES.ABOUT, screen_name: 'about' })
  }, [analytics, isAnalyticsReady, location])

  // Hero "signal field": faint dot lattice + a decision path that draws once. Ambient, cheap,
  // disabled under reduced-motion. Cleaned up on unmount.
  useEffect(() => {
    const c = canvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let raf = 0
    let W = 0
    let H = 0
    let t = 0
    const dots: Array<{ x: number; y: number; p: number }> = []
    for (let i = 0; i < 120; i++) {
      dots.push({ x: ((i * 97) % 100) / 100, y: ((i * 61) % 100) / 100, p: i % 7 })
    }
    const path = [
      [0.06, 0.78], [0.2, 0.62], [0.34, 0.68], [0.47, 0.44],
      [0.61, 0.52], [0.74, 0.3], [0.9, 0.38],
    ]
    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      W = c.clientWidth
      H = c.clientHeight
      c.width = W * dpr
      c.height = H * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    const draw = (): void => {
      ctx.clearRect(0, 0, W, H)
      for (const d of dots) {
        const x = d.x * W
        const y = d.y * H
        const pulse = reduce ? 0 : Math.sin(t * 0.6 + d.p) * 0.5 + 0.5
        const lit = d.p === 3
        ctx.beginPath()
        ctx.arc(x, y, lit ? 1.6 + pulse * 1.3 : 1.1, 0, 6.2832)
        ctx.fillStyle = lit
          ? `rgba(255,108,57,${0.28 + pulse * 0.5})`
          : 'rgba(180,168,148,0.16)'
        ctx.fill()
      }
      const total = path.length - 1
      const prog = reduce ? 1 : Math.min(1, t * 0.12)
      ctx.lineWidth = 1.4
      ctx.strokeStyle = 'rgba(255,108,57,0.5)'
      ctx.beginPath()
      for (let k = 0; k <= total * prog; k++) {
        const a = path[Math.floor(k)] || path[path.length - 1]
        const X = a[0] * W
        const Y = a[1] * H
        if (k === 0) ctx.moveTo(X, Y)
        else ctx.lineTo(X, Y)
      }
      ctx.stroke()
      const head = path[Math.min(path.length - 1, Math.floor(total * prog))]
      if (head) {
        ctx.beginPath()
        ctx.arc(head[0] * W, head[1] * H, 4, 0, 6.2832)
        ctx.fillStyle = '#FF6C39'
        ctx.fill()
        ctx.beginPath()
        ctx.arc(head[0] * W, head[1] * H, 9 + (reduce ? 0 : Math.sin(t * 1.4) * 3), 0, 6.2832)
        ctx.strokeStyle = 'rgba(255,108,57,0.28)'
        ctx.lineWidth = 1
        ctx.stroke()
      }
      t += 0.03
      if (!reduce) raf = requestAnimationFrame(draw)
    }
    resize()
    window.addEventListener('resize', resize)
    draw()
    return () => {
      window.removeEventListener('resize', resize)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  const arrow = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path d="M4 12h15M13 6l6 6-6 6" />
    </svg>
  )

  return (
    <div className="rsly-about">
      <Helmet>
        <title>{DocumentTitle}</title>
        <meta
          name="description"
          content="Resactly is a restaurant discovery app for diners and a decision layer for hospitality — built from the choices people make before a table is ever booked."
        />
      </Helmet>

      <style>{css}</style>

      <header className="ra-hero">
        <canvas ref={canvasRef} aria-hidden="true"></canvas>
        <div className="ra-wrap">
          <p className="ra-eyebrow">Intent Intelligence for Hospitality</p>
          <h1>
            Restaurants know what happened <span className="warm">after you sat down.</span>
            <br />
            Resactly knows how you <em>decided to come.</em>
          </h1>
          <p className="ra-sub">
            A discovery app for diners. A decision layer for the hospitality industry — built from
            the choices people make before a table is ever booked.
          </p>
          <div className="ra-paths">
            <a className="ra-btn ra-primary" href="#diners">Discover restaurants</a>
            <a className="ra-btn ra-ghost" href="#restaurants">Understand your diners</a>
          </div>
        </div>
      </header>

      <section className="ra-statement">
        <div className="ra-wrap">
          <p className="ra-eyebrow">The blind spot</p>
          <h2>Every restaurant can see the meal. None of them can see the deciding.</h2>
          <p className="ra-lead">
            Reviews are written after. Reservations are logged after. The register rings after. The
            choice itself — the comparing, the hesitating, the changing of minds — happens somewhere
            no one is looking.
          </p>
          <div className="ra-diptych">
            <div className="before">
              <span className="tag">Where the decision happens</span>
              <h3>Before</h3>
              <p>Browsing, comparing, lingering on a menu, weighing it against three other places, asking the group.</p>
              <p className="when">visible to: no one</p>
            </div>
            <div className="after">
              <span className="tag">What today&apos;s tools record</span>
              <h3>After</h3>
              <p>The booking. The order. The bill. The star rating left the next morning.</p>
              <p className="when">visible to: reviews · reservations · POS</p>
            </div>
          </div>
        </div>
      </section>

      <hr className="ra-rule" />

      <section>
        <div className="ra-wrap">
          <p className="ra-eyebrow">The model</p>
          <h2>One app. Two sides of the same moment.</h2>
          <p className="ra-lead">
            Diners get recommendations that learn from what people actually do — not what they rate.
            Every interaction leaves an anonymous trace of intent. In aggregate, those traces become
            the first clear picture of how dining decisions get made.
          </p>
          <div className="ra-flow">
            <div className="ra-node">
              <div className="k">Diner</div><h4>Discovers</h4>
              <p>Explores, compares, saves, dwells, plans a night out.</p>
            </div>
            <div className="ra-arrow">{arrow}</div>
            <div className="ra-node mid">
              <div className="k">Anonymous signal</div><h4>Intent</h4>
              <p>Every action, stripped of identity, becomes behavioral signal.</p>
            </div>
            <div className="ra-arrow">{arrow}</div>
            <div className="ra-node">
              <div className="k">Restaurant</div><h4>Understands</h4>
              <p>Sees demand, consideration, and the shape of the decision.</p>
            </div>
          </div>
        </div>
      </section>

      <hr className="ra-rule" />

      <section id="diners">
        <div className="ra-wrap ra-aud">
          <div className="copy">
            <p className="ra-eyebrow">For diners</p>
            <h2>Find the place you&apos;ll actually love — before you get there.</h2>
            <p className="ra-body">
              Recommendations built from behavior, not five-star noise. The more you explore, the
              sharper they get. <strong>No account required to start</strong> — and a menu-scan tells
              you what to order once you arrive.
            </p>
          </div>
          <div className="ra-phone" aria-hidden="true">
            <div className="screen">
              <div className="sbar"><i></i></div>
              <div className="cmini"><div className="row"><span className="nm">Casa Lupita</span><span className="mt">94% match</span></div><div className="sub">Oaxacan · 6 min · $$ · lively</div><div className="bar"><i style={{ width: '94%' }}></i></div></div>
              <div className="cmini"><div className="row"><span className="nm">Blue Hour</span><span className="mt">88% match</span></div><div className="sub">Wine bar · 11 min · $$$ · quiet</div><div className="bar"><i style={{ width: '88%' }}></i></div></div>
              <div className="cmini"><div className="row"><span className="nm">Sabi Ramen</span><span className="mt">81% match</span></div><div className="sub">Ramen · 4 min · $ · casual</div><div className="bar"><i style={{ width: '81%' }}></i></div></div>
              <div className="foot">learning from what you open, save &amp; linger on</div>
            </div>
          </div>
        </div>
      </section>

      <hr className="ra-rule" />

      <section id="groups">
        <div className="ra-wrap ra-aud flip">
          <div className="copy">
            <p className="ra-eyebrow">For groups</p>
            <h2>The hardest reservation is the one with friends.</h2>
            <p className="ra-body">
              Share a link. Everyone adds their taste — no downloads, no accounts. Resactly finds the
              place the whole table will love and turns a stalled group chat into{' '}
              <strong>a decision everyone can live with</strong>.
            </p>
          </div>
          <div className="ra-phone" aria-hidden="true">
            <div className="screen">
              <div className="sbar"><i></i></div>
              <div className="gtitle">Friday, 4 of us</div>
              <div className="grow"><span className="av"></span><span className="av"></span><span className="av"></span><span className="av"></span><span className="gtxt">4 tastes in</span></div>
              <div className="cmini"><div className="row"><span className="nm">Casa Lupita</span><span className="mt">the pick</span></div><div className="sub">works for vegetarian · lively · walkable</div><div className="bar"><i style={{ width: '100%' }}></i></div></div>
              <div className="cmini dim"><div className="row"><span className="nm">Blue Hour</span><span className="votes">2 votes</span></div><div className="sub">runner-up</div></div>
              <div className="reserve">Reserve for the table</div>
            </div>
          </div>
        </div>
      </section>

      <hr className="ra-rule" />

      <section className="ra-statement">
        <div className="ra-wrap">
          <p className="ra-eyebrow">Anonymous by design</p>
          <h2>You are not the product. The pattern is.</h2>
          <p className="ra-lead">
            Explore without an account. Nothing you do is tied to your name. What restaurants receive
            is the <strong className="ink">shape of demand in aggregate</strong> — never a person.
            Rewards are simply our half of that exchange: you help the industry see, and you earn for
            it.
          </p>
        </div>
      </section>

      <section className="ra-turn" id="restaurants">
        <div className="ra-wrap">
          <p className="ra-eyebrow">Intent intelligence</p>
          <h2>The layer beneath the reservation.</h2>
          <p className="ra-lead">
            The decision journey, made legible. Not who someone is —{' '}
            <strong className="ink">how a table gets chosen</strong>, at the scale of a whole market.
          </p>
          <div className="ra-signals">
            {SIGNALS.map((s, i) => (
              <div className="ra-sig" key={s.h}>
                <div className="n">{String(i + 1).padStart(2, '0')}</div>
                <h4>{s.h}</h4>
                <p>{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="ra-rule" />

      <section>
        <div className="ra-wrap">
          <p className="ra-eyebrow">The restaurant dashboard</p>
          <h2>See the deciding, not just the dining.</h2>
          <p className="ra-lead">
            The same behavior that sharpens a diner&apos;s recommendations gives a restaurant its
            first read on demand it never had a way to measure.
          </p>
          <div className="ra-dash" aria-hidden="true">
            <div className="top">
              <span className="dot"></span><span className="dot"></span><span className="dot"></span>
              <span className="title">resactly · intent</span>
              <span className="env">LIVE</span>
            </div>
            <div className="dgrid">
              <div className="cell">
                <div className="lbl">Decision journey — this week</div>
                <div className="funnel">
                  {FUNNEL.map((r) => (
                    <div className="fr" key={r.l}>
                      <span className="fl">{r.l}</span>
                      <span className="fb" style={{ width: `${r.w}%` }}></span>
                      <span className="fv">{r.v}</span>
                    </div>
                  ))}
                </div>
                <p className="cap">Where interest becomes intent — and where it doesn&apos;t.</p>
              </div>
              <div className="cell">
                <div className="lbl">Avg. consideration time</div>
                <div className="big">31<small>s</small></div>
                <div className="lbl mt">Attention by menu position</div>
                <div className="bars">
                  {BARS.map((b) => (
                    <div className="b" key={b.r}><span>{b.r}</span><i style={{ height: `${b.h}%` }}></i></div>
                  ))}
                </div>
                <div className="axis"><span>rank</span><span></span><span></span><span></span><span></span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="ra-rule" />

      <section className="ra-statement">
        <div className="ra-wrap">
          <p className="ra-eyebrow">Where we fit</p>
          <h2>Not a review site. Not a booking tool. Not a POS.</h2>
          <div className="ra-nots">
            <div className="not"><div className="x">Not this</div><h4>A review platform</h4></div>
            <div className="not"><div className="x">Not this</div><h4>A reservation system</h4></div>
            <div className="not"><div className="x">Not this</div><h4>A point of sale</h4></div>
          </div>
          <div className="ra-affirm">
            <div className="x">What it is</div>
            <h4>An intelligence layer for the deciding — the part of the meal that happens before anyone shows up.</h4>
          </div>
          <p className="ra-body mt">
            Those systems record what already happened. Resactly reads what&apos;s happening first:
            the intent that decides where the industry&apos;s next table gets booked.
          </p>
        </div>
      </section>

      <hr className="ra-rule" />

      <section>
        <div className="ra-wrap">
          <p className="ra-eyebrow">Where this goes</p>
          <h2 className="narrow">Every dining decision, understood — city by city.</h2>
          <p className="ra-lead">
            We started with recommendations. We&apos;re building the decision layer for hospitality —
            beginning with the most ambitious dining markets in the world, where understanding demand
            before it arrives is worth the most.
          </p>
        </div>
      </section>

      <section className="ra-cta">
        <div className="ra-wrap">
          <p className="ra-eyebrow">Two ways in</p>
          <h2>Discover restaurants you&apos;ll love. Help hospitality understand why.</h2>
          <p className="ra-lead">
            Resactly helps people find the right table — and helps the industry finally see how those
            decisions get made.
          </p>
          <div className="ra-paths">
            <a className="ra-btn ra-primary" href={ROUTES.HOME}>Get started</a>
            <a className="ra-btn ra-ghost" href={ROUTES.REWARDS_INFO}>See your rewards</a>
          </div>
        </div>
      </section>

      <footer className="ra-footer">
        <div className="ra-wrap">
          <span className="mark">Res<b>·</b>actly</span>
          <small>Intent intelligence for hospitality</small>
        </div>
      </footer>
    </div>
  )
}

const SIGNALS = [
  { h: 'Customer intent', p: "What diners are actually looking for, in the moment they're looking." },
  { h: 'Consideration', p: 'How long a place holds attention before a choice is made.' },
  { h: 'Competitive set', p: 'Which restaurants get weighed against yours, and when.' },
  { h: 'Recommendation lift', p: 'Whether being surfaced turns into visits — not just impressions.' },
  { h: 'Menu engagement', p: 'Which sections and prices pull people in or push them away.' },
  { h: 'Dish attention', p: 'The plates that make someone stop scrolling and decide.' },
  { h: 'Decision journey', p: 'The full path from first look to booked table.' },
  { h: 'Where it breaks', p: 'The exact step where interest quietly turns into a no.' },
]

const FUNNEL = [
  { l: 'surfaced', w: 100, v: '4,120' },
  { l: 'opened', w: 58, v: '2,390' },
  { l: 'menu viewed', w: 34, v: '1,401' },
  { l: 'considered', w: 19, v: '784' },
  { l: 'reserved', w: 9, v: '371' },
]

const BARS = [
  { r: '0', h: 92 }, { r: '1', h: 70 }, { r: '2', h: 44 }, { r: '3', h: 30 }, { r: '4', h: 18 },
]

const css = `
.rsly-about{
  --ground:#F1F0EB;--surface:#FBFAF7;--ink:#191510;--muted:#6C665C;--line:#D9D5CC;
  --signal:#DA4E1C;--signal-ink:#B23F14;--signal-wash:#DA4E1C14;
  --serif:'Fraunces',ui-serif,"New York","Iowan Old Style",Georgia,"Times New Roman",serif;
  --sans:'Geist Sans','Geist',-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,"Helvetica Neue",sans-serif;
  --mono:'JetBrains Mono',ui-monospace,"SF Mono","Cascadia Code",Menlo,monospace;
  background:var(--ground);color:var(--ink);font-family:var(--sans);font-size:17px;line-height:1.6;
  -webkit-font-smoothing:antialiased;overflow-x:hidden;
}
@media (prefers-color-scheme:dark){.rsly-about{
  --ground:#14110D;--surface:#1B1712;--ink:#EEE9E0;--muted:#9C9488;--line:#2C271F;
  --signal:#FF6C39;--signal-ink:#FF8256;--signal-wash:#FF6C3914;}}
.rsly-about *{box-sizing:border-box}
.rsly-about .ra-wrap{max-width:1120px;margin-inline:auto;padding-inline:clamp(1.25rem,5vw,3rem)}
.rsly-about section{padding-block:clamp(4.5rem,12vh,8.5rem);position:relative}
.rsly-about .ra-rule{height:1px;background:var(--line);border:0;margin:0}
.rsly-about h1,.rsly-about h2,.rsly-about h3{font-family:var(--serif);font-weight:500;text-wrap:balance;letter-spacing:-.015em}
.rsly-about h2{font-size:clamp(2rem,4.6vw,3.35rem);line-height:1.08;margin:0 0 1.1rem;max-width:20ch}
.rsly-about h2.narrow{max-width:16ch}
.rsly-about .ra-lead{font-size:clamp(1.08rem,1.7vw,1.32rem);color:var(--muted);max-width:62ch;line-height:1.5;margin:0}
.rsly-about .ra-lead .ink,.rsly-about .ra-body strong{color:var(--ink)}
.rsly-about p.ra-body{max-width:62ch;color:var(--muted)}
.rsly-about .mt{margin-top:1.6rem}
.rsly-about .ra-eyebrow{font-family:var(--mono);font-size:.72rem;letter-spacing:.2em;text-transform:uppercase;color:var(--muted);display:inline-flex;align-items:center;gap:.7rem;margin:0 0 1.4rem}
.rsly-about .ra-eyebrow::before{content:"";width:7px;height:7px;background:var(--signal);box-shadow:0 0 0 4px var(--signal-wash)}
.rsly-about .ra-btn{font-family:var(--sans);font-size:.95rem;font-weight:600;padding:.85rem 1.4rem;border-radius:2px;border:1px solid transparent;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;gap:.5rem;transition:transform .15s,background .15s,border-color .15s}
.rsly-about .ra-primary{background:var(--signal);color:#fff}
.rsly-about .ra-primary:hover{transform:translateY(-1px);background:var(--signal-ink)}
.rsly-about .ra-ghost{background:transparent;color:inherit;border-color:currentColor;opacity:.82}
.rsly-about .ra-ghost:hover{opacity:1;transform:translateY(-1px)}
.rsly-about .ra-btn:focus-visible{outline:2px solid var(--signal);outline-offset:3px}
.rsly-about .ra-hero{background:#100D0A;color:#EFE9DF;position:relative;overflow:hidden;padding-block:clamp(5.5rem,16vh,10rem);border-bottom:1px solid #241E17}
.rsly-about .ra-hero canvas{position:absolute;inset:0;width:100%;height:100%;display:block}
.rsly-about .ra-hero .ra-wrap{position:relative;z-index:2}
.rsly-about .ra-hero .ra-eyebrow{color:#B6AA97}
.rsly-about .ra-hero h1{font-size:clamp(2.5rem,6.2vw,5.1rem);line-height:1.04;margin:0 0 1.6rem;max-width:20ch;color:#F4EFE6}
.rsly-about .ra-hero h1 em{font-style:italic;color:#FF7A47}
.rsly-about .ra-sub{font-family:var(--sans);font-size:clamp(1.05rem,1.7vw,1.3rem);color:#B6AA97;max-width:46ch;line-height:1.5;margin:0 0 2.4rem}
.rsly-about .ra-paths{display:flex;flex-wrap:wrap;gap:.8rem}
.rsly-about .ra-statement h2{max-width:18ch}
.rsly-about .ra-diptych{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--line);border:1px solid var(--line);border-radius:4px;overflow:hidden;margin-top:2.6rem}
@media (max-width:620px){.rsly-about .ra-diptych{grid-template-columns:1fr}}
.rsly-about .ra-diptych>div{background:var(--surface);padding:clamp(1.5rem,3vw,2.2rem)}
.rsly-about .ra-diptych .tag{font-family:var(--mono);font-size:.7rem;letter-spacing:.16em;text-transform:uppercase;color:var(--muted)}
.rsly-about .ra-diptych .before{background:linear-gradient(180deg,var(--signal-wash),transparent)}
.rsly-about .ra-diptych .before .tag{color:var(--signal-ink)}
.rsly-about .ra-diptych h3{font-size:1.3rem;margin:.5rem 0 .4rem;font-family:var(--serif);font-weight:500}
.rsly-about .ra-diptych p{margin:0;color:var(--muted);font-size:.96rem}
.rsly-about .ra-diptych .when{font-family:var(--mono);font-size:.72rem;margin-top:1rem;opacity:.7}
.rsly-about .ra-flow{display:grid;grid-template-columns:1fr auto 1fr auto 1fr;align-items:stretch;margin-top:2.5rem}
@media (max-width:760px){.rsly-about .ra-flow{grid-template-columns:1fr}.rsly-about .ra-arrow{transform:rotate(90deg);padding:.4rem 0}}
.rsly-about .ra-node{background:var(--surface);border:1px solid var(--line);border-radius:4px;padding:1.4rem 1.5rem}
.rsly-about .ra-node .k{font-family:var(--mono);font-size:.68rem;letter-spacing:.16em;text-transform:uppercase;color:var(--muted)}
.rsly-about .ra-node h4{font-family:var(--serif);font-weight:500;font-size:1.24rem;margin:.5rem 0 .35rem}
.rsly-about .ra-node p{margin:0;font-size:.9rem;color:var(--muted);line-height:1.45}
.rsly-about .ra-node.mid{border-color:var(--signal);box-shadow:0 0 0 1px var(--signal),0 12px 30px -18px var(--signal)}
.rsly-about .ra-node.mid .k{color:var(--signal-ink)}
.rsly-about .ra-arrow{display:flex;align-items:center;justify-content:center;padding:0 .9rem;color:var(--signal)}
.rsly-about .ra-arrow svg{width:26px;height:26px}
.rsly-about .ra-aud{display:grid;grid-template-columns:.9fr 1.1fr;gap:clamp(2rem,5vw,4rem);align-items:center}
.rsly-about .ra-aud.flip{grid-template-columns:1.1fr .9fr}
.rsly-about .ra-aud.flip .copy{order:2}
@media (max-width:820px){.rsly-about .ra-aud,.rsly-about .ra-aud.flip{grid-template-columns:1fr}.rsly-about .ra-aud.flip .copy{order:0}}
.rsly-about .ra-phone{justify-self:center;width:260px;max-width:78vw;aspect-ratio:9/18.5;border-radius:30px;background:var(--surface);border:1px solid var(--line);box-shadow:0 40px 80px -50px #0009;padding:14px}
.rsly-about .ra-phone .screen{height:100%;border-radius:20px;background:linear-gradient(180deg,var(--ground),var(--surface));border:1px solid var(--line);overflow:hidden;display:flex;flex-direction:column}
.rsly-about .ra-phone .sbar{height:26px;display:flex;align-items:center;justify-content:center}
.rsly-about .ra-phone .sbar i{width:44px;height:4px;border-radius:3px;background:var(--line)}
.rsly-about .cmini{margin:8px 10px;padding:10px;border-radius:10px;background:var(--surface);border:1px solid var(--line)}
.rsly-about .cmini.dim{opacity:.7}
.rsly-about .cmini .row{display:flex;justify-content:space-between;align-items:center;gap:8px}
.rsly-about .cmini .nm{font-weight:600;font-size:.8rem}
.rsly-about .cmini .mt{font-family:var(--mono);font-size:.62rem;color:#fff;background:var(--signal);padding:2px 6px;border-radius:20px;margin-top:0}
.rsly-about .cmini .votes{font-family:var(--mono);font-size:.62rem;color:var(--muted)}
.rsly-about .cmini .sub{font-size:.66rem;color:var(--muted);margin-top:3px}
.rsly-about .cmini .bar{height:5px;border-radius:4px;background:var(--line);margin-top:8px;overflow:hidden}
.rsly-about .cmini .bar>i{display:block;height:100%;background:var(--signal)}
.rsly-about .ra-phone .foot{margin-top:auto;padding:10px 12px;font-family:var(--mono);font-size:.6rem;color:var(--muted)}
.rsly-about .ra-phone .gtitle{padding:12px 12px 0;font-family:var(--serif);font-size:1rem}
.rsly-about .grow{display:flex;align-items:center;gap:6px;margin:10px}
.rsly-about .grow .av{width:22px;height:22px;border-radius:50%;border:2px solid var(--surface);background:linear-gradient(135deg,var(--signal),#f0a);margin-left:-8px}
.rsly-about .grow .av:first-child{margin-left:0}
.rsly-about .grow .gtxt{font-size:.66rem;color:var(--muted);margin-left:6px}
.rsly-about .ra-phone .reserve{margin:auto 12px 12px;padding:9px;border-radius:9px;background:var(--signal);color:#fff;text-align:center;font-size:.72rem;font-weight:600}
.rsly-about .ra-turn{background:var(--surface);border-block:1px solid var(--line)}
.rsly-about .ra-signals{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:var(--line);border:1px solid var(--line);border-radius:4px;overflow:hidden;margin-top:2.6rem}
@media (max-width:900px){.rsly-about .ra-signals{grid-template-columns:repeat(2,1fr)}}
@media (max-width:480px){.rsly-about .ra-signals{grid-template-columns:1fr}}
.rsly-about .ra-sig{background:var(--surface);padding:1.3rem 1.35rem;min-height:118px}
.rsly-about .ra-sig .n{font-family:var(--mono);font-size:.68rem;color:var(--muted);opacity:.6}
.rsly-about .ra-sig h4{font-family:var(--sans);font-size:.98rem;font-weight:650;margin:.55rem 0 .3rem;letter-spacing:-.01em}
.rsly-about .ra-sig p{margin:0;font-size:.82rem;color:var(--muted);line-height:1.4}
.rsly-about .ra-dash{background:#0F0C09;color:#EAE4D9;border-radius:10px;border:1px solid #241E17;overflow:hidden;box-shadow:0 50px 90px -60px #000;margin-top:2.4rem}
.rsly-about .ra-dash .top{display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1px solid #241E17}
.rsly-about .ra-dash .dot{width:9px;height:9px;border-radius:50%;background:#2f2820}
.rsly-about .ra-dash .top .title{font-family:var(--mono);font-size:.72rem;letter-spacing:.1em;color:#9C9488;margin-left:6px}
.rsly-about .ra-dash .top .env{margin-left:auto;font-family:var(--mono);font-size:.66rem;color:#0F0C09;background:#FF6C39;padding:3px 8px;border-radius:20px}
.rsly-about .ra-dash .dgrid{display:grid;grid-template-columns:1.3fr 1fr;gap:1px;background:#241E17}
@media (max-width:720px){.rsly-about .ra-dash .dgrid{grid-template-columns:1fr}}
.rsly-about .ra-dash .cell{background:#0F0C09;padding:16px 18px}
.rsly-about .ra-dash .cell .lbl{font-family:var(--mono);font-size:.64rem;letter-spacing:.12em;text-transform:uppercase;color:#8A8277}
.rsly-about .ra-dash .cell .lbl.mt{margin-top:14px}
.rsly-about .ra-dash .cell .big{font-family:var(--serif);font-size:2rem;margin:6px 0 2px}
.rsly-about .ra-dash .cell .big small{font-size:.9rem;color:#9C9488;font-family:var(--mono)}
.rsly-about .ra-dash .funnel{display:flex;flex-direction:column;gap:7px;margin-top:12px}
.rsly-about .ra-dash .fr{display:flex;align-items:center;gap:10px}
.rsly-about .ra-dash .fr .fl{font-family:var(--mono);font-size:.66rem;color:#9C9488;width:92px;flex:none}
.rsly-about .ra-dash .fr .fb{height:16px;border-radius:3px;background:linear-gradient(90deg,#FF6C39,#FF9366)}
.rsly-about .ra-dash .fr .fv{font-family:var(--mono);font-size:.68rem;color:#EAE4D9;font-variant-numeric:tabular-nums}
.rsly-about .ra-dash .bars{display:flex;align-items:flex-end;gap:8px;height:96px;margin-top:12px}
.rsly-about .ra-dash .bars .b{flex:1;background:#2A2118;border-radius:3px 3px 0 0;position:relative}
.rsly-about .ra-dash .bars .b>i{position:absolute;inset:auto 0 0 0;background:linear-gradient(180deg,#FF6C39,#B23F14);border-radius:3px 3px 0 0}
.rsly-about .ra-dash .bars .b span{position:absolute;top:-16px;left:0;right:0;text-align:center;font-family:var(--mono);font-size:.6rem;color:#9C9488}
.rsly-about .ra-dash .axis{display:flex;gap:8px;margin-top:6px}
.rsly-about .ra-dash .axis span{flex:1;text-align:center;font-family:var(--mono);font-size:.58rem;color:#6f6659}
.rsly-about .ra-dash .cap{font-size:.78rem;color:#8A8277;margin:10px 2px 0}
.rsly-about .ra-nots{display:grid;grid-template-columns:repeat(3,1fr);gap:1rem;margin:2.4rem 0 2.2rem}
@media (max-width:680px){.rsly-about .ra-nots{grid-template-columns:1fr}}
.rsly-about .not{border:1px dashed var(--line);border-radius:4px;padding:1.3rem 1.4rem}
.rsly-about .not .x{font-family:var(--mono);font-size:.7rem;letter-spacing:.14em;text-transform:uppercase;color:var(--muted)}
.rsly-about .not h4{font-family:var(--serif);font-weight:500;font-size:1.3rem;margin:.4rem 0 0;text-decoration:line-through;text-decoration-color:var(--signal);text-decoration-thickness:2px;opacity:.72}
.rsly-about .ra-affirm{border:1px solid var(--signal);background:var(--signal-wash);border-radius:4px;padding:1.6rem 1.7rem}
.rsly-about .ra-affirm .x{font-family:var(--mono);font-size:.7rem;letter-spacing:.14em;text-transform:uppercase;color:var(--signal-ink)}
.rsly-about .ra-affirm h4{font-family:var(--serif);font-weight:500;font-size:1.55rem;margin:.35rem 0 0}
.rsly-about .ra-cta{background:#100D0A;color:#EFE9DF;text-align:center;border-top:1px solid #241E17}
.rsly-about .ra-cta h2{max-width:20ch;margin-inline:auto;color:#F4EFE6}
.rsly-about .ra-cta .ra-lead{margin-inline:auto;color:#B6AA97}
.rsly-about .ra-cta .ra-paths{justify-content:center;margin-top:2.2rem}
.rsly-about .ra-footer{padding:2.5rem 0 3.5rem}
.rsly-about .ra-footer .ra-wrap{display:flex;flex-wrap:wrap;gap:1rem;justify-content:space-between;align-items:center}
.rsly-about .ra-footer .mark{font-family:var(--serif);font-size:1.15rem}
.rsly-about .ra-footer .mark b{color:var(--signal);font-weight:500}
.rsly-about .ra-footer small{font-family:var(--mono);font-size:.68rem;color:var(--muted);letter-spacing:.06em}
@media (prefers-reduced-motion:reduce){.rsly-about *{animation:none!important;transition:none!important}}
`

export default About
